import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useMemo } from "react";
import { cva } from "class-variance-authority";
import { ScoreDetail } from "@/features/vision-360/types";
import { Sparkles } from "lucide-react";
import { getScoreColorName } from "@/features/vision-360/utils/getScoreIndicator";
import { translateCriterion } from "@/features/vision-360/utils/translateCriterion";

export interface ScoreAnalysis {
  criterion: string;
  requested: string;
  offered: string;
  matched: boolean;
  score?: number;
  inTolerance?: boolean;
}

export interface ScoreDetails {
  scoreDetails: ScoreAnalysis[] | null;
  details?: ScoreDetail | null;
}
interface ScoreDetailsModalProps {
  scoreDetails: ScoreDetails;
  match: number;
  handleClose: () => void;
}

export const ScoreDetailsModal = ({
  scoreDetails: { scoreDetails, details },
  match,
  handleClose,
}: ScoreDetailsModalProps) => {
  // Extract first numeric token — handles "min 50", "max 207360", "3.8km", "5km radius"
  const formatValue = (criterion: string, value: string) => {
    if (!value) return "—";
    const numMatch = value.match(/\d+(\.\d+)?/);
    const n = numMatch ? parseFloat(numMatch[0]) : NaN;
    if (criterion.includes("price")) {
      if (!isNaN(n) && n > 0)
        return `R$ ${Math.round(n).toLocaleString("pt-BR")}`;
      return value;
    }
    if (criterion.includes("capacity")) {
      if (!isNaN(n)) return `${Math.round(n)} pessoas`;
      return value;
    }
    if (criterion === "distance") {
      if (!isNaN(n)) return `${n}km`;
      return value;
    }
    return value;
  };

  // If backend didn't split the detail yet, do it client-side as fallback
  const resolveColumns = (detail: ScoreAnalysis) => {
    const req = detail.requested ?? "";
    const off = detail.offered ?? "";
    if (req === off && req.includes(" / ")) {
      const idx = req.indexOf(" / ");
      return {
        offered: req.substring(0, idx),
        requested: req.substring(idx + 3),
      };
    }
    return { offered: off, requested: req };
  };

  const matchColor = useMemo(() => getScoreColorName(match) || "red", [match]);

  const variants = cva("rounded-md px-2 py-0.5", {
    variants: {
      color: {
        green: "bg-[#9AE600]",
        yellow: "bg-[#FFB86A]",
        red: "bg-[#FF6467]",
      },
    },
  });

  const visibleCriteria =
    scoreDetails?.filter(
      (d) =>
        !["Status", "Recent Update", "status", "update", "photos"].includes(
          d.criterion,
        ),
    ) ?? [];

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent
        className="p-6 w-[90vw] max-w-[1000px]"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader className="mb-6">
          <DialogTitle className="text-xl">
            Como esse score foi calculado?
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Entenda por que essa sala combina (ou não) com o que você procura.
          </p>
        </DialogHeader>

        <div className="space-y-6">
          <div className="border rounded-lg">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="border-b">
                  <th className="px-4 py-3 text-sm font-medium text-left">
                    Critério
                  </th>
                  <th className="px-4 py-3 text-sm font-medium text-left">
                    O que você pediu
                  </th>
                  <th className="px-4 py-3 text-sm font-medium text-left">
                    O que a sala oferece
                  </th>
                  <th className="px-4 py-3 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {visibleCriteria.map((detail, index) => {
                  const cols = resolveColumns(detail);
                  return (
                    <tr key={index} className="border-b last:border-b-0">
                      <td className="px-4 py-3 text-sm">
                        {translateCriterion(detail.criterion)}
                        {detail.inTolerance && (
                          <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                            tolerância
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {formatValue(detail.criterion, cols.requested) ||
                          "Não informado"}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {formatValue(detail.criterion, cols.offered)}
                      </td>
                      <td className="px-4 py-3">
                        <div
                          className={`w-4 h-4 rounded-full ${
                            detail.matched
                              ? "bg-green-500"
                              : detail.inTolerance
                                ? "bg-amber-400"
                                : "bg-red-500"
                          }`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {details?.explanation && (
            <div className="px-4 py-4 bg-[#F9E10D]/10 rounded-lg border border-[#F9E10D]/30">
              <div className="flex items-start gap-2 mb-3">
                <Sparkles className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-sm text-gray-600 mb-2">
                    Análise Vision
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {details.explanation.summary}
                  </p>
                  {details.explanation.bullets.length > 0 && (
                    <ul className="space-y-1.5 mb-3">
                      {details.explanation.bullets.map((bullet, index) => (
                        <li key={index} className="text-sm text-gray-700">
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-sm font-medium text-gray-900">
                    {details.explanation.recommendation}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between border-t pt-4">
            <p className="text-sm font-medium">
              {visibleCriteria.filter((d) => d.matched).length} de{" "}
              {visibleCriteria.length} critérios atendidos
            </p>
            <Badge
              variant="secondary"
              className={`${variants({ color: matchColor })}`}
            >
              Score {match}%
            </Badge>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
